import config from '../../config/config';
import {
  CreateCardPoolPayload,
  CreateTokenPayload,
  RechargeCardPoolPayload,
  RequestCardPoolsInterface,
  RequestLedgerEntriesInterface,
  RequestTokensInterface,
  UpdateCardPoolPayload,
  UpdateTokenPayload,
} from '../../interface/rfid-management.interface';

import deleteRequest from './base/delete';
import getRequest from './base/get';
import patchRequest from './base/patch';
import postRequest from './base/post';

/**
 * RFID and Tap Card Management API utility object
 */
export default {
  /**
   * Fetches tokens / RFID cards list from EMSP
   * @param {RequestTokensInterface} parameters - Query filters and pagination
   * @returns {Promise<any>} Response with tokens list
   */
  fetchTokens: (parameters: RequestTokensInterface = {}): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/tokens`, {
      ...parameters,
    });
  },

  /**
   * Fetches tokens / RFID cards associated with a specific customer
   * @param {string} customerId - Customer identifier or userCustomId
   * @param {any} [parameters] - Optional query parameters
   * @returns {Promise<any>} Response with customer tokens list
   */
  fetchTokensByCustomer: (customerId: string, parameters: any = {}): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/tokens/customer/${customerId}`, {
      ...parameters,
    });
  },

  /**
   * Fetches tokens / RFID cards list for export
   * @param {RequestTokensInterface} [parameters] - Query filters
   * @returns {Promise<any>} Response with tokens export list
   */
  fetchTokensExport: (parameters: RequestTokensInterface = {}): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/tokens/export`, {
      ...parameters,
    });
  },

  /**
   * Fetches single RFID token details by ID (_id) from EMSP API
   * @param {string} tokenId - ID or _id of the token
   * @returns {Promise<any>} Response with token details
   */
  fetchTokenById: (tokenId: string): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/tokens/${tokenId}`);
  },

  /**
   * Fetches filter options for token list
   * @returns {Promise<any>} Response with filter list
   */
  fetchTokensFilterList: (): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/tokens/list-filters`);
  },

  /**
   * Fetches all RFID tokens from EMSP API
   * @returns {Promise<any>} Response with all tokens array
   */
  fetchAllTokens: (): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/tokens/all`);
  },

  /**
   * Creates a new RFID token in EMSP
   * @param {CreateTokenPayload} data - Token creation payload
   * @returns {Promise<any>} Response with created token
   */
  createToken: (data: CreateTokenPayload): Promise<any> => {
    return postRequest(`${config.emsp.emspApiUrl}api/v1/tokens`, data);
  },

  /**
   * Updates an existing RFID token (e.g. assigned stations or linked customer) in EMSP
   * @param {string} tokenId - ID of the token to update
   * @param {UpdateTokenPayload} data - Token update payload
   * @returns {Promise<any>} Response with updated token
   */
  updateToken: (tokenId: string, data: UpdateTokenPayload): Promise<any> => {
    return patchRequest(`${config.emsp.emspApiUrl}api/v1/tokens/${tokenId}`, data);
  },

  /**
   * Deactivates an RFID token permanently (terminal deactivation)
   * @param {string} tokenId - ID of the token to deactivate
   * @param {any} [data] - Optional deactivation payload
   * @returns {Promise<any>} Response with deactivated token
   */
  deactivateToken: async (tokenId: string, data: any = {}): Promise<any> => {
    return patchRequest(`${config.emsp.emspApiUrl}api/v1/tokens/${tokenId}/deactivate`, data);
  },

  /**
   * Changes the activation status of an RFID token
   * @param {string} tokenId - ID of the token to update
   * @param {any} [data] - Optional update payload
   * @returns {Promise<any>} Response with updated token
   */
  changeActivateToken: async (tokenId: string, data: any = {}): Promise<any> => {
    return patchRequest(`${config.emsp.emspApiUrl}api/v1/tokens/${tokenId}/status-change`, data);
  },

  /**
   * Deletes an RFID token
   * @param {string} tokenId - ID of the token to delete
   * @returns {Promise<any>} Response after deletion
   */
  deleteToken: (tokenId: string): Promise<any> => {
    return deleteRequest(`${config.emsp.emspApiUrl}api/v1/tokens/${tokenId}`);
  },

  /**
   * Fetches card pools list with query parameters from EMSP
   * @param {RequestCardPoolsInterface} parameters - Query filters and pagination
   * @returns {Promise<any>} Response with card pools list
   */
  fetchCardPools: (parameters: RequestCardPoolsInterface = {}): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/card-pools`, {
      ...parameters,
    });
  },

  /**
   * Fetches card pools list for export
   * @param {RequestCardPoolsInterface} [parameters] - Query filters
   * @returns {Promise<any>} Response with card pools export list
   */
  fetchCardPoolsExport: (parameters: RequestCardPoolsInterface = {}): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/card-pools/export`, {
      kind: 'GROUP',
      ...parameters,
    });
  },

  /**
   * Fetches filter options for card pools list
   * @returns {Promise<any>} Response with filter list
   */
  fetchCardPoolsFilterList: (): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/card-pools/list-filters`);
  },

  /**
   * Fetches single card pool / group details by ID (_id) from EMSP API
   * @param {string} cardPoolId - ID or _id of the card pool
   * @returns {Promise<any>} Response with card pool details
   */
  fetchCardPoolById: (cardPoolId: string): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/card-pools/${cardPoolId}`);
  },

  /**
   * Creates a new card pool
   * @param {CreateCardPoolPayload} data - Card pool creation payload
   * @returns {Promise<any>} Response with created card pool
   */
  createCardPool: (data: CreateCardPoolPayload): Promise<any> => {
    return postRequest(`${config.emsp.emspApiUrl}api/v1/card-pools`, data);
  },

  /**
   * Updates an existing card pool (e.g. name, stations, assigned tokens) in EMSP
   * @param {string} cardPoolId - ID of the card pool to update
   * @param {UpdateCardPoolPayload} data - Card pool update payload
   * @returns {Promise<any>} Response with updated card pool
   */
  updateCardPool: (cardPoolId: string, data: UpdateCardPoolPayload): Promise<any> => {
    return patchRequest(`${config.emsp.emspApiUrl}api/v1/card-pools/${cardPoolId}`, data);
  },

  /**
   * Recharges energy balance on a card pool via EMSP API
   * @param {RechargeCardPoolPayload} data - Card pool recharge payload
   * @returns {Promise<any>} Response with updated card pool
   */
  rechargeCardPool: (data: RechargeCardPoolPayload): Promise<any> => {
    const { cardPoolId, ...body } = data;
    return postRequest(`${config.emsp.emspApiUrl}api/v1/card-pools/${cardPoolId}/recharge`, body);
  },

  /**
   * Fetches ledger entries list with query parameters
   * @param {RequestLedgerEntriesInterface} parameters - Query filters and pagination
   * @returns {Promise<any>} Response with ledger entries list
   */
  fetchLedgerEntries: (parameters: RequestLedgerEntriesInterface = {}): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/ledgers`, {
      ...parameters,
    });
  },

  /**
   * Fetches ledger entries list for export
   * @param {RequestLedgerEntriesInterface} [parameters] - Query filters
   * @returns {Promise<any>} Response with ledger entries export list
   */
  fetchLedgerEntriesExport: (parameters: RequestLedgerEntriesInterface = {}): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/ledgers/export`, {
      ...parameters,
    });
  },

  /**
   * Fetches filter options for ledger entries list
   * @returns {Promise<any>} Response with filter list
   */
  fetchLedgerFilterList: (): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/ledgers/list-filters`);
  },

  /**
   * Fetches single ledger entry details by ID (_id) from EMSP API
   * @param {string} ledgerId - ID or _id of the ledger entry
   * @returns {Promise<any>} Response with ledger entry details
   */
  fetchLedgerById: (ledgerId: string): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/ledgers/${ledgerId}`);
  },

  /**
   * Fetches individual type list for Business Tap assignment (Mock API)
   * @param {any} parameters - Optional query filters
   * @returns {Promise<any>} Response with individual list
   */
  fetchIndividualList: async (parameters: any = {}): Promise<any> => {
    try {
      return await getRequest(`${config.commonApiUrl}api/super-admin/v1/rfid/individuals`, {
        ...parameters,
      });
    } catch {
      return {
        data: [
          { label: 'John Doe - Driver (ind_01)', value: 'ind_01' },
          { label: 'Sarah Connor - Executive (ind_02)', value: 'ind_02' },
          { label: 'Michael Smith - Logistics (ind_03)', value: 'ind_03' },
          { label: 'Alex Johnson - Technician (ind_04)', value: 'ind_04' },
          { label: 'David Miller - Sales Lead (ind_05)', value: 'ind_05' },
        ],
      };
    }
  },

  /**
   * Fetches group type list for Business Tap assignment from EMSP API
   * @param {any} parameters - Optional query filters
   * @returns {Promise<any>} Response with group list
   */
  fetchGroupList: (parameters: any = {}): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/card-pools`, {
      kind: 'GROUP',
      ...parameters,
    });
  },

  /**
   * Fetches customer list from EMSP for Personal Tap card assignment
   * @param {any} parameters - Optional query filters
   * @returns {Promise<any>} Response with customer list
   */
  fetchCustomers: (parameters: any = {}): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/customers`, {
      ...parameters,
    });
  },

  /**
   * Fetches customer account list from EMSP for Personal Tap card assignment
   * @param {any} parameters - Optional query filters
   * @returns {Promise<any>} Response with customer account list
   */
  fetchCustomerAccountList: (parameters: any = {}): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/customers`, {
      ...parameters,
    });
  },

  /**
   * Fetches station list from EMSP for card and card pool assignment
   * @param {any} parameters - Optional query filters
   * @returns {Promise<any>} Response with station list
   */
  fetchStationsList: (parameters: any = {}): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/stations`, {
      ...parameters,
    });
  },

  /**
   * Fetches RFID summary statistics from EMSP API
   * @returns {Promise<any>} Response with RFID summary statistics
   */
  fetchRfidSummary: (): Promise<any> => {
    return getRequest(`${config.emsp.emspApiUrl}api/v1/rfid/summary`);
  },
};
